import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { SERVICES, WA_LINKS } from "../../data/siteData";

const NAV_LINKS = [
  { id: "home", label: "Home", to: "/", testId: "nav-home" },
  { id: "services", label: "Services", dropdown: true, testId: "nav-services" },
  { id: "case-studies", label: "Case Studies", to: "/case-studies", testId: "nav-case-studies" },
  { id: "about", label: "About", to: "/about", testId: "nav-about" },
  { id: "contact", label: "Contact", to: "/contact", testId: "nav-contact" },
];

const ServicesDropdown = ({ open, onToggle, dropdownRef }) => (
  <div ref={dropdownRef} className="relative">
    <button
      type="button"
      data-testid="nav-services"
      aria-expanded={open}
      aria-haspopup="true"
      onClick={onToggle}
      className="inline-flex items-center gap-1 text-[#C0C0C0] hover:text-white transition-colors whitespace-nowrap"
    >
      Services
      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
    </button>
    {open && (
      <div
        data-testid="nav-services-dropdown"
        className="absolute left-0 top-full mt-3 w-72 bg-[#0A192F] border border-[#C0C0C0]/20 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7)] py-2"
      >
        <Link
          to="/services"
          data-testid="nav-services-all"
          className="block px-5 py-2.5 text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#1E3A8A]/30 transition-colors"
        >
          All Services
        </Link>
        {SERVICES.map((s) => (
          <Link
            key={s.slug}
            to={`/services/${s.slug}`}
            data-testid={`nav-service-${s.slug}`}
            className="block px-5 py-2.5 text-sm text-[#C0C0C0] hover:text-white hover:bg-[#1E3A8A]/30 transition-colors"
          >
            {s.shortName}
          </Link>
        ))}
      </div>
    )}
  </div>
);

const MobileMenu = () => (
  <div
    data-testid="mobile-menu"
    className="lg:hidden bg-[#0A192F]/97 backdrop-blur-md border-t border-[#C0C0C0]/15 max-h-[calc(100vh-4rem)] overflow-y-auto"
  >
    <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
      <Link to="/" data-testid="mobile-nav-home" className="text-base font-medium text-[#C0C0C0] hover:text-white py-1">
        Home
      </Link>
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#D4AF37] font-semibold py-1">Services</div>
        <div className="mt-1 flex flex-col gap-2.5 border-l border-[#C0C0C0]/20 pl-4">
          <Link to="/services" data-testid="mobile-nav-services-all" className="text-sm text-[#C0C0C0] hover:text-white py-0.5">
            All Services
          </Link>
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`mobile-nav-service-${s.slug}`}
              className="text-sm text-[#C0C0C0] hover:text-white py-0.5"
            >
              {s.shortName}
            </Link>
          ))}
        </div>
      </div>
      <Link to="/case-studies" data-testid="mobile-nav-case-studies" className="text-base font-medium text-[#C0C0C0] hover:text-white py-1">
        Case Studies
      </Link>
      <Link to="/about" data-testid="mobile-nav-about" className="text-base font-medium text-[#C0C0C0] hover:text-white py-1">
        About
      </Link>
      <Link to="/contact" data-testid="mobile-nav-contact" className="text-base font-medium text-[#C0C0C0] hover:text-white py-1">
        Contact
      </Link>
      <a
        href={WA_LINKS.recommend}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="mobile-nav-cta"
        className="mt-3 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-6 py-3.5 text-sm font-semibold border border-[#D4AF37] w-full"
      >
        Get My AXIS Recommendation
      </a>
    </div>
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    const onEscape = (e) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEscape);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEscape);
    };
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-[#0A192F]/95 backdrop-blur-md border-b border-[#C0C0C0]/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2 shrink-0"
          aria-label="AXIS Business Solutions — Home"
        >
          <img
            src="/assets/axis-logo-light.png"
            alt="AXIS Business Solutions"
            className="h-9 md:h-11 w-auto"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8 text-sm mx-4" aria-label="Main navigation">
          {NAV_LINKS.map((l) =>
            l.dropdown ? (
              <ServicesDropdown
                key={l.id}
                open={dropdownOpen}
                onToggle={() => setDropdownOpen((v) => !v)}
                dropdownRef={dropdownRef}
              />
            ) : (
              <Link
                key={l.id}
                to={l.to}
                data-testid={l.testId}
                className="text-[#C0C0C0] hover:text-white transition-colors whitespace-nowrap"
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={WA_LINKS.recommend}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A192F] px-5 py-2.5 text-sm font-semibold border border-[#D4AF37] hover:bg-transparent hover:text-[#D4AF37] transition-all duration-300 whitespace-nowrap"
          >
            Get My AXIS Recommendation
          </a>
          <button
            data-testid="nav-mobile-toggle"
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-white p-2 -mr-2"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && <MobileMenu />}
    </header>
  );
};

export default Navbar;
